import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Subscription } from "@/types/subscription";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";
import { getStatusBadgeStyle } from "@/components/clients/ClientUtils";
import { getStatusText } from "@/utils/clientUtils";
import { fr, enUS } from "date-fns/locale";
import { Crown } from "lucide-react";
import { useCurrency } from "@/contexts/CurrencyContext";

interface SubscriptionHistoryAccordionProps {
  subscriptions: Subscription[];
  paymentsBySubscription: Record<string, { amount: number; payment_date: string }[]>;
  featured?: boolean;
}

export function SubscriptionHistoryAccordion({
  subscriptions,
  paymentsBySubscription,
  featured = false,
}: SubscriptionHistoryAccordionProps) {
  const { t, i18n } = useTranslation(["subscriptions", "common"]);
  const locale = i18n.language === "fr" ? fr : enUS;
  const { formatCurrency } = useCurrency();

  if (featured) {
    if (!subscriptions.length) return null;
    return (
      <section className="athlete-subscription-history border border-border/70 bg-card/90 p-4 md:p-6">
        <h2 className="text-2xl font-bold md:text-3xl">{t("subscriptions:subscriptionHistory")}</h2>
        <p className="mt-1 text-sm text-muted-foreground">{t("subscriptions:subscriptionHistoryHint")}</p>
        <Accordion type="single" collapsible className="mt-5 space-y-3">
          {subscriptions.map((sub) => {
            const id = String(sub.id ?? `${sub.type}-${sub.start_date}`);
            const payments = sub.id ? paymentsBySubscription[sub.id] ?? [] : [];
            const totalPaid = payments.reduce((acc, payment) => acc + payment.amount, 0);
            const remaining = sub.price - totalPaid;
            return (
              <AccordionItem key={id} value={id} className="athlete-subscription-history-item border border-border/70 bg-background/60 px-4">
                <AccordionTrigger className="gap-3 py-4 hover:no-underline">
                  <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-md border border-primary/60 bg-primary/10 text-primary"><Crown className="h-6 w-6" /></span>
                  <span className="min-w-0 flex-1 text-left">
                    <span className="block truncate font-bold">{t(`plans.${sub.type.toLowerCase()}.title`, { ns: "subscriptions", defaultValue: sub.type })}</span>
                    <span className="mt-1 block text-xs font-normal text-muted-foreground md:text-sm">{format(new Date(sub.start_date), "PPP", { locale })} – {format(new Date(sub.end_date), "PPP", { locale })}</span>
                  </span>
                  <Badge variant="secondary" className="shrink-0 rounded-full px-3">{t(getStatusText(sub.status), { ns: "common" })}</Badge>
                </AccordionTrigger>
                <AccordionContent className="border-t border-border/50 pb-4 pt-4">
                  <div className="grid grid-cols-2 gap-3 text-sm md:grid-cols-3">
                    <KeyValue label={t("subscriptions:sessions.remaining")} value={`${sub.sessions_remaining} / ${sub.sessions_total}`} />
                    <KeyValue label={t("subscriptions:amountPaid")} value={formatCurrency(totalPaid)} />
                    <KeyValue label={t("subscriptions:amountRemaining")} value={remaining > 0 ? formatCurrency(remaining) : t("subscriptions:fullyPaid")} valueClass={remaining > 0 ? "text-destructive" : "text-chart-2"} />
                  </div>
                </AccordionContent>
              </AccordionItem>
            );
          })}
        </Accordion>
      </section>
    );
  }

  return (
    <Card className="bg-card/60 backdrop-blur-xl border border-border/40 shadow-lg">
      <CardHeader>
        <CardTitle>{t("subscriptions:subscriptionHistory")}</CardTitle>
      </CardHeader>
      <CardContent>
        <Accordion type="single" collapsible className="w-full">
          {subscriptions.map((sub) => {
            const payments = paymentsBySubscription[sub.id] || [];
            const totalPaid = payments.reduce((acc, p) => acc + p.amount, 0);
            const remaining = sub.price - totalPaid;

            return (
              <AccordionItem key={sub.id} value={sub.id} className="border-border/30">
                <AccordionTrigger className="text-left">
                  <div className="flex flex-col w-full gap-1">
                    <div className="flex items-center justify-between">
                      <span className="font-semibold text-base truncate">
                        {t(`plans.${sub.type.toLowerCase()}.title`, { ns: "subscriptions", defaultValue: sub.type })}
                      </span>
                      <Badge className={getStatusBadgeStyle(sub.status)}>
                        {t(getStatusText(sub.status), { ns: "common" })}
                      </Badge>
                    </div>
                    <span className="text-sm text-muted-foreground">
                      {format(new Date(sub.start_date), "PPP", { locale })} → {format(new Date(sub.end_date), "PPP", { locale })}
                    </span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pt-4 space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                    <KeyValue label={t("subscriptions:sessions.remaining")} value={`${sub.sessions_remaining} / ${sub.sessions_total}`} />
                    <KeyValue label={t("subscriptions:jokers.remaining")} value={`${sub.jokers_remaining} / ${sub.jokers_total}`} />
                    <KeyValue label={t("subscriptions:amountTotal")} value={`${sub.price.toFixed(2)} DT`} />
                    <KeyValue label={t("subscriptions:amountPaid")} value={`${totalPaid.toFixed(2)} DT`} />
                    <KeyValue
                      label={t("subscriptions:amountRemaining")}
                      value={remaining > 0 ? `${remaining.toFixed(2)} DT` : t("subscriptions:fullyPaid")}
                      valueClass={remaining > 0 ? "text-destructive" : "text-green-600"}
                    />
                  </div>

                  {payments.length > 0 && (
                    <div className="mt-2">
                      <p className="font-semibold mb-1">{t("subscriptions:paymentHistory")}</p>
                      <ul className="space-y-1 text-sm text-muted-foreground">
                        {payments.map((p, index) => (
                          <li key={index} className="flex justify-between">
                            <span>{format(new Date(p.payment_date), "PPP", { locale })}</span>
                            <span>{p.amount.toFixed(2)} DT</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </AccordionContent>
              </AccordionItem>
            );
          })}
        </Accordion>
      </CardContent>
    </Card>
  );
}

const KeyValue = ({ label, value, valueClass = "" }: { label: string; value: string; valueClass?: string }) => (
  <div className="flex flex-col bg-muted/20 backdrop-blur-sm rounded-lg p-2.5">
    <p className="text-xs text-muted-foreground">{label}</p>
    <p className={`text-base font-medium ${valueClass}`}>{value}</p>
  </div>
);
